import type { PendingAttachment } from '../stores/appStore';

interface AttachmentPreviewBarProps {
  attachments: PendingAttachment[];
  onRemove: (id: string) => void;
  sentCount?: number;
  maxCount?: number;
}

function getAttachmentBadgeLabel(attachment: PendingAttachment): string {
  if (attachment.previewKind === 'audio') {
    return 'MP3';
  }

  const parts = attachment.fileName.split('.');
  const extension = parts.length > 1 ? parts[parts.length - 1] : 'FILE';
  return extension.toUpperCase().slice(0, 4);
}

export default function AttachmentPreviewBar({
  attachments,
  onRemove,
  sentCount = 0,
  maxCount,
}: AttachmentPreviewBarProps) {
  const totalCount = sentCount + attachments.length;
  const approachingLimit = typeof maxCount === 'number' && totalCount >= maxCount - 2 && totalCount < maxCount;
  const limitReached = typeof maxCount === 'number' && totalCount >= maxCount;
  const compressedAttachments = attachments.filter(
    (attachment) => attachment.wasCompressed && attachment.compressionSummary,
  );

  return (
    <div className="space-y-2 rounded-xl border px-3 py-2" style={{ borderColor: 'var(--theme-border)', background: 'var(--theme-card)' }}>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {attachments.map((attachment) => (
          <div key={attachment.id} className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg border" style={{ borderColor: 'var(--theme-border)' }}>
            {attachment.dataUrl ? (
              <img
                src={attachment.dataUrl}
                alt={attachment.fileName}
                className="h-12 w-12 object-cover"
              />
            ) : (
              <div
                className="flex h-12 w-12 items-center justify-center px-1 text-center text-[10px] font-semibold"
                style={{
                  background: 'var(--theme-secondary)',
                  color: 'var(--theme-secondary-foreground)',
                }}
                title={attachment.fileName}
              >
                {getAttachmentBadgeLabel(attachment)}
              </div>
            )}
            {attachment.wasCompressed ? (
              <div className="absolute bottom-0 left-0 right-0 px-1 py-0.5 text-center text-[9px] font-medium" style={{ background: 'color-mix(in srgb, var(--theme-background) 78%, transparent)', color: 'var(--theme-foreground)' }}>
                Compressed
              </div>
            ) : null}
            <button
              type="button"
              onClick={() => onRemove(attachment.id)}
              className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full border text-xs"
              style={{
                background: 'var(--theme-background)',
                color: 'var(--theme-foreground)',
                borderColor: 'var(--theme-border)',
              }}
              aria-label={`Remove ${attachment.fileName}`}
            >
              ×
            </button>
          </div>
        ))}
      </div>

      <div className="text-xs" style={{ color: 'var(--theme-muted-foreground)' }}>
        {typeof maxCount === 'number'
          ? `${totalCount} / ${maxCount} files this session`
          : `${attachments.length} file${attachments.length === 1 ? '' : 's'} attached`}
      </div>

      {compressedAttachments.length > 0 ? (
        <div className="space-y-1">
          {compressedAttachments.map((attachment) => (
            <div key={`compression-${attachment.id}`} className="text-xs" style={{ color: 'var(--theme-muted-foreground)' }}>
              <span style={{ color: 'var(--theme-foreground)', fontWeight: 600 }}>{attachment.fileName}</span>: {attachment.compressionSummary}
            </div>
          ))}
        </div>
      ) : null}

      {approachingLimit ? (
        <div className="text-xs" style={{ color: 'var(--theme-primary)' }}>Approaching file limit</div>
      ) : null}

      {limitReached ? <div className="text-xs" style={{ color: 'var(--theme-destructive, #ff6b6b)' }}>File limit reached</div> : null}
    </div>
  );
}
